import * as tz from 'date-fns-tz/esm/fp';
import * as fns from 'date-fns/fp';
import { function as F, option as O, reader as R } from 'fp-ts';
import { AppDate, isValidAppDate, parseAppDate } from './date';
import { AppDateTime, isValidAppDateTime, parseAppDateTime } from './datetime';
import { AppTime, isValidAppTime, parseAppTime } from './time';

export type DateInput = AppTime | AppDate | AppDateTime | number | Date;

export function isDateInput(
  value: DateInput | string | null | undefined,
): value is DateInput {
  return F.pipe(
    value,
    O.fromNullable,
    O.fold(
      () => false,
      F.flow(
        value => (typeof value === 'string' ? O.some(value) : O.none),
        O.filterMap(value => (isValidAppDateTime(value) ? O.none : O.some(value))),
        O.filterMap(value => (isValidAppDate(value) ? O.none : O.some(value))),
        O.filterMap(value => (isValidAppTime(value) ? O.none : O.some(value))),
        O.fold(
          () => true,
          () => false,
        ),
      ),
    ),
  );
}

export function toDate<T extends DateInput | undefined | null | string>(
  input: T,
): T extends DateInput ? Date : T {
  switch (typeof input) {
    case 'number':
      return F.pipe(
        input * 60 * 60 * 1000,
        fns.toDate,
        tz.utcToZonedTime('UTC'),
      ) as T extends DateInput ? Date : T;
    case 'string':
      const inputNumeric = parseInt(input.trim(), 10);
      if (inputNumeric.toString() === input.trim()) {
        return toDate(inputNumeric) as T extends DateInput ? Date : T;
      }

      if (isValidAppDateTime(input as string)) {
        return F.pipe(input as AppDateTime, parseAppDateTime) as T extends DateInput
          ? Date
          : T;
      }

      if (isValidAppDate(input as string)) {
        return F.pipe(input as AppDate, parseAppDate) as T extends DateInput ? Date : T;
      }

      if (isValidAppTime(input as string)) {
        return F.pipe(
          input as unknown as AppTime,
          parseAppTime,
          seconds => seconds * 1000,
          fns.toDate,
          tz.utcToZonedTime('UTC'),
        ) as T extends DateInput ? Date : T;
      }

      return input as T extends DateInput ? Date : T;
    default:
      return input as T extends DateInput ? Date : T;
  }
}

export function toDateOption<T extends DateInput | undefined | null | string>(
  input: T,
): T extends DateInput ? O.Some<Date> : O.None {
  return F.pipe(input, toDate, value =>
    value instanceof Date ? O.some(value) : O.none,
  ) as T extends DateInput ? O.Some<Date> : O.None;
}

export const formatInTZ = F.pipe(
  R.ask<string>(),
  R.map(format =>
    F.pipe(R.ask<Date>(), R.map(F.flow(tz.utcToZonedTime('UTC'), fns.format(format)))),
  ),
);
